import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Container,
  SxProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useAppSelector } from '../../../../hooks';
import { checkUserPermission } from '../../../../helper';
import { getAuthState } from '../../../../store/AuthSlice';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../../components/BreadCrumb/Breadcrumbs';
import TabBar, { ITabItem } from '../../../../components/V2/Tabs/type2/TabBar';
import CompletedTab from './Tabs/CompletedTab';
import RescheduledOrCancelled from './Tabs/RescheduledOrCancelledTab';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../store/V2/RolesSlice';
import PaymentAdjustmentsTab from './Tabs/PaymentAdjustmentsTab';
import ManualAdjustmentsTab from './Tabs/ManualAdjustmentsTab';
import { getTenantsState } from '../../../../store/V2/TenantsSlice';

export type TState = {
  selectedTab: ITabItem | null;
  tabs: ITabItem[];
};

const tableCardContainerStyles: SxProps = {
  borderRadius: '20px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
};

const tabOptions: ITabItem[] = [
  {
    index: 0,
    key: 'completed',
    title: 'Completed',
  },
  {
    index: 1,
    key: 'rescheduledOrCancelled',
    title: 'Rescheduled / Cancelled',
  },
  {
    index: 2,
    key: 'paymentAdjustments',
    title: 'Payment Adjustments',
  },
  {
    index: 3,
    key: 'manualAdjustments',
    title: 'Manual Adjustments',
  },
];

const initialState: TState = {
  selectedTab: null,
  tabs: [],
};

const AdminLessonHistoryPage: FunctionComponent = () => {
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  // State
  const [state, setState] = useState({ ...initialState });

  useEffect(() => {
    const newTabOptions = tabOptions.filter((item) => {
      if (
        item.key === 'completed' &&
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            adminLessonHistoryTabs: [
              USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB,
            ],
          },
        })
      ) {
        return true;
      }

      if (
        item.key === 'rescheduledOrCancelled' &&
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            adminLessonHistoryTabs: [
              USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_RESCHEDULE_OR_CANCELLATION_TAB,
            ],
          },
        })
      ) {
        return true;
      }

      if (
        item.key === 'paymentAdjustments' &&
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            adminPenaltyWaivers: [
              USER_PERMISSION_INDIVIDUALS.ADMIN_PENALTY_WAIVERS_VIEW,
            ],
          },
        })
      ) {
        return true;
      }

      if (
        item.key === 'manualAdjustments' &&
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            adminManualAdjustments: [
              USER_PERMISSION_INDIVIDUALS.ADMIN_MANUAL_ADJUSTMENTS_VIEW,
            ],
          },
        })
      ) {
        return true;
      }

      return false;
    });

    updateStateAttributes({
      tabs: newTabOptions,
      selectedTab: newTabOptions.length ? newTabOptions[0] : null,
    });
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const constructBreadcrumbOptions = () => {
    const options: Array<BreadcrumbOption> = [
      {
        type: 'link',
        title: 'Home',
        link: `${process.env.PUBLIC_URL}`,
      },
      {
        type: 'text',
        title: 'Lesson History',
        highlight: true,
      },
    ];

    return options;
  };

  const renderTabPanel = () => {
    let tabPanel: JSX.Element | null = null;

    if (state.selectedTab) {
      switch (state.selectedTab.key) {
        case 'completed':
          tabPanel = <CompletedTab />;
          break;

        case 'rescheduledOrCancelled':
          tabPanel = <RescheduledOrCancelled />;
          break;

        case 'paymentAdjustments':
          tabPanel = <PaymentAdjustmentsTab />;
          break;

        case 'manualAdjustments':
          tabPanel = <ManualAdjustmentsTab />;
          break;

        default:
          tabPanel = null;
          break;
      }
    }

    return tabPanel;
  };

  const breadcrumbOptions = constructBreadcrumbOptions();

  return (
    <Container maxWidth="xl">
      <div style={{ padding: '30px 0' }}>
        <Breadcrumbs
          options={breadcrumbOptions}
          textColor="#647488"
          showDropDown={isXsScreen}
        />
      </div>
      {state.tabs.length ? (
        <div>
          <Card sx={tableCardContainerStyles}>
            <CardContent sx={{ p: 0 }}>
              <div style={{ borderBottom: '2px solid #1E293B14' }}>
                <div style={{ padding: '0 10px' }}>
                  <TabBar
                    selectedTab={state.selectedTab || undefined}
                    tabs={state.tabs}
                    onChange={(e, value) =>
                      updateStateAttributes({ selectedTab: state.tabs[value] })
                    }
                  />
                </div>
              </div>
              <div>{renderTabPanel()}</div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </Container>
  );
};

export default AdminLessonHistoryPage;
